/**
 * Party-room wire protocol (docs/design/multiplayer.md §5), shared by the browser and the Room
 * Durable Object (apps/server/src/room.ts). JSON over one WebSocket per member.
 *
 * Netcode: the room runs the authoritative sim + bots at 20 Hz and sends, for every tick, the
 * commands it applied (`f`). Clients replay them on their own copy of the deterministic sim;
 * `h` (every HASH_EVERY ticks) lets them detect drift and ask for a snapshot (`snap`).
 *
 * Pure: no DOM, no clock.
 */
import type { AffixId, BotSkillPreset, ClassId, Command, RunResult, UnitId, World } from '@keystone/sim';
import { CHAT_MAX_LEN } from '@keystone/sim';
import type { CompiledCharacter } from '@keystone/progression';

/** Bump when the sim, content or this protocol changes incompatibly (rooms refuse other builds). */
export const NET_BUILD = 'keystone-net-25'; // 25: +glide (gliding, falls, sky-puzzle platforms and updrafts: Command 'glide', Unit.jump.air / Unit.perch, DungeonDef.airspace, the 'glide' event, FieldState z / AN_GLIDE, sky secrets); 24: +grid (the Waystone Grid: grid stars, ability stars, notables and keystones compiled into ClassDefs (grid.<class>.* auras), StatModKind gatherSpeedPct, profile v13); 23: +controls (action controls: a human moving up to ~100° off its facing is a full-speed strafe, only more is backpedal; sim backpedalFactor); 22: +elev / +edge / +ore / +spawn (Mossvale relief, ridges and road cuts, the organic mountain-edge boundary, rocky outcrops, ore by rock, Granny Pell's start and the rerouted main quest, profile v12); 21: +quests (side quests: 18 quest NPCs on the Mossvale map, intents questAccept / questTurnIn / questAbandon, FieldGain.quests, profile v11); 20: +prof (gathering professions: FieldDef.gatherNodes, FieldState.gather, the 'setGatherSkill' command, interact 'gather:<id>', the 'gather' event, profile v10); 19: +detail (Mossvale's points of interest: the Hollowcrown, the Old Beacon, the hermit's hut, the Ring of Hours, the Stone King, the angler's and the abandoned camps, a wayside shrine, roadside ruins, two farm windmills: new obstacles, lore NPCs and secrets); 18: +specs final balance (Grave Tithe, Sentenced, the Lurk-like key 3 out of combat, Phantom Lure's strike, coefficient changes) and +presence waystone travel (field event 'travel', validated and snapped by the shard); 17: +zone v2 (Mossvale 864 × 810, the 1–10 journey; FieldDef.waystones / dormantYd, interact 'waystone:<id>', the 'waystone' event, nav obstacle buckets, profile v9); 16: +ranger-specs (the Ranger specialisations: S8 selfAuraElapsed, AuraDef.tally, AreaSpec.lowestHp, persistent pets: SummonEffect.companion / NpcDef.companion / Unit.companion / companionCast, the spirit beast NPCs); 15: +acolyte-specs (the Acolyte specialisations: S6 AuraDef.transfer, S7 SummonEffect.pet / Unit.pet, S10 AuraDef.damageShare, the Hollow One NPC); 14: +mage-specs / +rogue-specs (the Mage and Rogue specialisations: S9 cheatDeath, S11 spendAll / perSpent / perSpentTargets, aura echoes); 13: +specs (class specialisations: SpecDef / applySpec, spec-keyed classes "warrior@ironclad", selfHarm / leech / cannotDie / perMissingHp / immuneSlow, the practice dummy, profile v8 specId); 12: +autoattack (WoW-retail auto-attack: 'autoAttack' command, Unit.autoAttack, dual wield / Auto Shot / staff swings, swing pauses); 11: +zone (Mossvale is one open map 'mossvale'; field areas / secrets / respawn rules / patrol spawns; AI sleep far from heroes; nav cell cache); 10: base kits levels 1–7 (key 7, kill credit, stealth, traps, travelling zones, maxHpPct, oocSettle); 9: +presence shared-field protocol (net/fieldProtocol.ts, /api/field/…/ws); 8: the five base classes (warrior, acolyte, rogue, mage, ranger), 'backward' / 'behindTarget' dashes; 7: Phase 1 (field mode, levels, setLevel, ability locks, normal difficulty, field loot, the 'jump' command); 3: human backpedal speed (sim rule); 4: Venom Altar v2 layout (rooms, obstacles, packs, timer); 5: mob movement (patrols, wanderers, links, call for help, LoS aggro, leash, timer); 6: personas (bot chat / AFK / wandering, 'say' / 'presence' / 'leaveParty' commands, bot names, tank waits for away members)
export const HASH_EVERY = 20;
export const MAX_HUMANS = 4;
/** Commands per client message / messages per second the room accepts. */
export const MAX_CMDS_PER_MSG = 16;
export const MAX_MSGS_PER_SEC = 60;

/** Party codes: 5 letters without look-alikes (I, L, O). */
export const PARTY_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ';
export const PARTY_CODE_RE = /^[A-HJKMNP-Z]{5}$/;

export function newPartyCode(rand: () => number = Math.random): string {
  let s = '';
  for (let i = 0; i < 5; i++) s += PARTY_CODE_ALPHABET[Math.floor(rand() * PARTY_CODE_ALPHABET.length) % PARTY_CODE_ALPHABET.length];
  return s;
}

export function normalizePartyCode(input: string): string | null {
  const c = input.trim().toUpperCase();
  return PARTY_CODE_RE.test(c) ? c : null;
}

/** Invite path prefix: `/p/CODE` (the Worker's SPA fallback serves index.html for it). */
export const INVITE_PATH = '/p/';

/** The shareable invite link on whatever host serves the game (workers.dev or a custom domain). */
export function inviteUrl(origin: string, code: string): string {
  return `${origin.replace(/\/$/, '')}${INVITE_PATH}${code}`;
}

/**
 * The party code in an invite: a URL with `/p/CODE` or `?party=CODE` (pasted or the page's own
 * location), or a bare code. Null when there is none.
 */
export function parseInvite(input: string): string | null {
  const text = input.trim();
  const bare = normalizePartyCode(text);
  if (bare) return bare;
  let url: URL;
  try {
    url = new URL(text, 'https://x.invalid');
  } catch {
    return null;
  }
  const q = url.searchParams.get('party');
  if (q) return normalizePartyCode(q);
  const m = /\/p\/([A-Za-z]{5})\/?$/.exec(url.pathname);
  return m ? normalizePartyCode(m[1]!) : null;
}

// ---------------------------------------------------------------------------
// Room view (lobby)
// ---------------------------------------------------------------------------

export interface RoomMemberView {
  playerId: string;
  /** Hero name of their main class ("Shino"). */
  name: string;
  classId: ClassId;
  ready: boolean;
  connected: boolean;
  leader: boolean;
  /** Another member already plays this class (must switch hero before starting). */
  conflict: boolean;
}

export interface RoomKey {
  dungeonId: string;
  level: number;
  affixes: AffixId[] | 'auto';
}

export interface RoomView {
  code: string;
  phase: 'lobby' | 'starting' | 'running';
  members: RoomMemberView[];
  /** Class per slot (PARTY_ORDER) and who plays it (null = bot). */
  slots: { classId: ClassId; playerId: string | null }[];
  key: RoomKey;
  /** Highest key the leader may start. */
  maxKey: number;
  /** Why the leader can't start yet (null = can start). */
  blocked: string | null;
}

// ---------------------------------------------------------------------------
// Run start
// ---------------------------------------------------------------------------

export interface NetRunStart {
  build: string;
  seed: number;
  dungeonId: string;
  level: number;
  affixes: AffixId[];
  /** Slot order (tank, healer, dps, dps). */
  classIds: ClassId[];
  names: string[];
  controllers: ('human' | 'bot')[];
  /** Who plays each slot (null = bot). */
  players: (string | null)[];
  /** Compiled characters in slot order: clients build the identical registry from these. */
  compiled: CompiledCharacter[];
  looterId: UnitId | null;
  botPreset: BotSkillPreset;
  /** The receiving client's slot (-1: not in this run). */
  you: number;
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export type ClientMsg =
  | { t: 'hello'; build: string; localDate?: string }
  | { t: 'ready'; on: boolean }
  | { t: 'key'; key: RoomKey }
  | { t: 'start' }
  /** `s`: sequence number of this batch (the room acks it in a frame's `a`; net/predict.ts). */
  | { t: 'cmd'; c: Command[]; s?: number }
  | { t: 'resync' }
  | { t: 'refresh' }
  | { t: 'leave' }
  | { t: 'ping'; n: number };

export interface NetFrame {
  t: 'f';
  /** world.tick after the step that applied `c`. */
  k: number;
  c: Command[];
  /**
   * Controller hand-offs applied to the world before this tick's commands: a bot takes over a
   * disconnected player's hero ('bot': bots lead pulls only for bot-controlled tanks) and hands
   * it back on reconnect. Applied identically on every client (see applyHandoffs).
   */
  x?: Handoff[];
  /** Acks: [slot, seq] of the last command batch from that slot applied in this tick. */
  a?: [number, number][];
  /** hashWorld after the step (every HASH_EVERY ticks). */
  h?: number;
  /** Client only: local time the frame arrived (never sent; net/netSession.ts learns the tick phase). */
  at?: number;
}

export interface Handoff {
  u: UnitId;
  c: 'human' | 'bot';
}

/** Apply a frame's controller hand-offs at the tick boundary (server and clients alike). */
export function applyHandoffs(w: World, x: readonly Handoff[] | undefined): void {
  for (const h of x ?? []) {
    const u = w.units[h.u];
    if (u && u.controller !== 'ai') u.controller = h.c;
  }
}

export interface NetEnd {
  t: 'end';
  result: RunResult;
  /** This member's rewards (handlers.committedBody), absent when they could not be saved. */
  version?: number;
  profile?: string;
  summary?: unknown;
  claims?: unknown;
  error?: string;
}

export type ServerMsg =
  | { t: 'welcome'; playerId: string }
  | { t: 'room'; room: RoomView }
  | { t: 'start'; run: NetRunStart }
  | { t: 'snap'; k: number; world: World }
  | NetFrame
  | NetEnd
  | { t: 'abort'; reason: string }
  | { t: 'error'; code: string; msg: string }
  | { t: 'pong'; n: number };

// ---------------------------------------------------------------------------
// Determinism helpers
// ---------------------------------------------------------------------------

/** FNV-1a over every unit's hp and position (exact floats) and the tick: cheap drift detector. */
export function hashWorld(w: Readonly<World>): number {
  let h = 0x811c9dc5;
  const mix = (s: string): void => {
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
  };
  mix(String(w.tick));
  for (const id of w.unitOrder) {
    const u = w.units[id];
    if (!u) continue;
    mix(`${id}:${u.hp}:${u.pos.x}:${u.pos.y}`);
  }
  return h >>> 0;
}

/** Same command content (prediction reconcile across the wire, where identity is lost). */
export function sameCommand(a: unknown, b: unknown): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** +presence: a chat line from another player: control chars to spaces, trimmed, capped (CHAT_MAX_LEN); null when empty. */
export function cleanChatText(raw: string): string | null {
  const text = raw.replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, CHAT_MAX_LEN);
  return text || null;
}
const shortStr = (v: unknown): v is string => typeof v === 'string' && v.length <= 64;
const vec = (v: unknown): { x: number; y: number } | null => {
  const o = v as { x?: unknown; y?: unknown } | null;
  return o && typeof o === 'object' && finite(o.x) && finite(o.y) ? { x: o.x, y: o.y } : null;
};

/**
 * A client command, rebuilt field by field for `actorId` (the sender's own unit), or null when
 * malformed. Only the Command union's own fields survive.
 */
export function sanitizeCommand(raw: unknown, actorId: UnitId): Command | null {
  const c = raw as Record<string, unknown> | null;
  if (!c || typeof c !== 'object') return null;
  switch (c['type']) {
    case 'move': {
      const out: Command = { type: 'move', actorId };
      const dir = vec(c['dir']);
      const dest = vec(c['dest']);
      if (dir) {
        const len = Math.hypot(dir.x, dir.y);
        out.dir = len > 1.0001 ? { x: dir.x / len, y: dir.y / len } : dir;
      }
      if (dest) out.dest = dest;
      return dir || dest ? out : null;
    }
    case 'stop':
      return { type: 'stop', actorId };
    case 'face': {
      const out: Command = { type: 'face', actorId };
      if (finite(c['facing'])) out.facing = c['facing'];
      if (shortStr(c['targetId'])) out.targetId = c['targetId'];
      return out.facing !== undefined || out.targetId !== undefined ? out : null;
    }
    case 'target':
      return c['targetId'] === null ? { type: 'target', actorId, targetId: null } : shortStr(c['targetId']) ? { type: 'target', actorId, targetId: c['targetId'] } : null;
    case 'cast': {
      if (!shortStr(c['abilityId'])) return null;
      const out: Command = { type: 'cast', actorId, abilityId: c['abilityId'] };
      if (shortStr(c['targetId'])) out.targetId = c['targetId'];
      const point = vec(c['point']);
      if (point) out.point = point;
      return out;
    }
    case 'cancelCast':
      return { type: 'cancelCast', actorId };
    case 'jump': // +jump
      return { type: 'jump', actorId };
    case 'glide': // +glide: open / fold the sender's own glider
      return typeof c['on'] === 'boolean' ? { type: 'glide', actorId, on: c['on'] } : null;
    case 'autoAttack': // +autoattack: Attack / right-click, always for the sender's own unit
      if (typeof c['on'] !== 'boolean') return null;
      return shortStr(c['targetId']) ? { type: 'autoAttack', actorId, on: c['on'], targetId: c['targetId'] } : { type: 'autoAttack', actorId, on: c['on'] };
    case 'interact': {
      const out: Command = { type: 'interact', actorId };
      if (shortStr(c['targetId'])) out.targetId = c['targetId'];
      if (shortStr(c['what'])) out.what = c['what'];
      return out;
    }
    // +personas: party chat (always as the sender's own unit; no line-pool ids from clients).
    case 'say': {
      const raw = c['text'];
      if (typeof raw !== 'string') return null;
      const text = cleanChatText(raw); // +presence: shared with zone chat (net/fieldProtocol.ts)
      return text ? { type: 'say', actorId, text, ...(c['emote'] === true ? { emote: true } : {}) } : null;
    }
    // +personas: a human may flag themselves AFK (never "wandering", never leave the group: that is a takeover).
    case 'presence':
      return c['status'] === 'afk' || c['status'] === null ? { type: 'presence', actorId, status: c['status'] } : null;
    default:
      return null;
  }
}
